/**
 * The frame around every signed-in screen: navigation, the account menu, the
 * offline queue bar, the notification bell, and the two switches in the header.
 *
 * Nothing here is an enum label. The nav items happen to read like the domain
 * — "Prijave", "Zadaci" — but they are route names this client owns, not the
 * `submissionTypes` and `taskStatuses` the server sends. The distinction is
 * worth holding on to: rename a route label here and nothing breaks; copy a
 * status label here and it silently stops matching the badge beside it.
 */

export const shell = {
  // ---- Brand --------------------------------------------------------
  // The line under the wordmark, and the alt text on the sign-in drawing.
  // The product name itself is never translated — see Logo.tsx.
  'brand.tagline': 'Kontrola i unapređenje',
  'brand.illustrationAlt': 'Skeniranje QR naljepnice na opremi u pogonu',

  // ---- Language and theme -------------------------------------------
  // The switcher's own chrome. The language *names* are not here: they come
  // from /api/meta as endonyms, so each stays readable to the person looking
  // for it regardless of what the interface is currently set to.
  'language.label': 'Jezik',
  'language.change': 'Promijeni jezik',

  'theme.light': 'Svijetla tema',
  'theme.dark': 'Tamna tema',
  'theme.system': 'Prema sistemu',
  /*
   * One string, not three fragments joined in JSX.
   *
   * The separator and the word "sljedeće" used to sit in the markup with the
   * two labels interpolated around them, which reads fine in Bosnian and is
   * unbuildable in any language that wants the parts in another order. A
   * translator needs the whole sentence, with the movable pieces named.
   */
  'theme.tooltip': '{current} · sljedeće: {next}',

  /*
   * Navigation.
   *
   * Held as keys rather than strings because `NAV` is a module-level constant —
   * there is no hook to call up there — so each entry names a key and the
   * component resolves it at render. That is also what makes the labels change
   * on a language switch without remounting the shell.
   */
  'nav.home': 'Početna',
  'nav.home.hint': 'Pregled i brze akcije',
  'nav.submissions': 'Prijave',
  'nav.submissions.hint': 'Sve prijave i njihovi statusi',
  'nav.tasks': 'Zadaci',
  'nav.tasks.hint': 'Zadaci dodijeljeni vama',
  'nav.fiveS': '5S',
  'nav.fiveS.hint': 'Ocjena radnih centara',
  'nav.analytics': 'Analitika',
  'nav.analytics.hint': 'Trendovi i analiza procesa',
  'nav.admin': 'Administracija',
  'nav.admin.hint': 'Korisnici, šifarnici i dnevnik',
  'nav.guide': 'Uputstvo',
  'nav.guide.hint': 'Kako se koristi, za vašu ulogu',
  // The way back to the owner console, not the way in — see the NAV entry.
  'nav.owner': 'Nazad u panel',
  'nav.owner.hint': 'Sve firme i podrška',
  'nav.adminBadge': 'ADMIN',
  'nav.mainLabel': 'Glavna navigacija',

  // ---- Account menu -------------------------------------------------
  'account.myAccount': 'Moj nalog',
  // Interpolated: the name is a person, and every language puts it somewhere
  // different relative to the word for "account".
  'account.ariaLabel': 'Nalog — {name}',
  'account.signOut': 'Odjava',

  // ---- Header actions -----------------------------------------------
  'header.scanQr': 'Skeniraj QR kod',
  'header.scanQr.hint': 'Skeniraj kod na mašini',
  'header.newSubmission': 'Nova prijava',

  // ---- Notifications ------------------------------------------------
  'notifications.title': 'Obavještenja',
  'notifications.empty': 'Nema obavještenja',
  'notifications.emptyHint':
    'Ovdje stižu zadaci koji vam se dodijele i promjene na vašim prijavama.',
  'notifications.allRead': 'Sve je pročitano',
  'notifications.markAllRead': 'Označi sve kao pročitano',
  'notifications.today': 'Danas',
  'notifications.yesterday': 'Juče',
  'notifications.earlier': 'Ranije',
  'notifications.unreadMarker': 'nepročitano',
  /*
   * Three forms, because that is how many the language has: 1 / 2–4 / 5+.
   *
   * They were previously chosen in the component by a `count === 1 ? … : …`
   * chain, which is the English rule and gets "2 nepročitane" wrong. Keeping
   * all three as separate keys lets each language declare its own set —
   * English fills `one` and leaves the other two saying the same thing, which
   * is correct for English rather than a gap.
   */
  'notifications.unread.one': '{count} nepročitano',
  'notifications.unread.few': '{count} nepročitane',
  'notifications.unread.many': '{count} nepročitanih',

  // ---- Offline queue ------------------------------------------------
  'queue.title': 'Red čekanja',
  'queue.savedOnDevice': 'Prijave se čuvaju na uređaju.',
  'queue.hint': 'Prijave se šalju automatski. Ovdje možete provjeriti šta je ostalo.',
  'queue.offlineNotice':
    'Trenutno nema veze sa serverom. Prijave su sačuvane na uređaju i biće poslate čim se veza vrati — možete zatvoriti aplikaciju.',
  'queue.empty': 'Red čekanja je prazan.',
  'queue.waiting': 'Čeka',
  'queue.rejectedBadge': 'Odbijena',
  'queue.sendNow': 'Pošalji sada',
  'queue.details': 'Detalji',
  'queue.remove': 'Ukloni',
  'queue.noConnection': 'Nema veze.',
  // Abbreviated on purpose — it sits in a cramped metadata line under the
  // title. "pril." does not decline, which is the one reason a count may sit
  // beside a noun here without three forms behind it.
  'queue.attachments': '{count} pril.',
  'queue.discardConfirm':
    'Ukloniti ovu prijavu iz reda čekanja? Neće biti poslata i podaci se gube.',
  /*
   * Full sentences, so the offline banner can put "Nema veze." in front of one
   * without gluing a phrase together.
   *
   * The component previously chose between two forms with
   * `count === 1 ? … : …`, which is the English rule — and both branches of the
   * pending one had been left saying the same thing, so "2 prijave čekaju" has
   * never once rendered correctly. Three keys and `tp()` are what fix it.
   */
  'queue.pending.one': '{count} prijava čeka slanje.',
  'queue.pending.few': '{count} prijave čekaju slanje.',
  'queue.pending.many': '{count} prijava čeka slanje.',
  'queue.rejected.one': '{count} prijava nije prihvaćena.',
  'queue.rejected.few': '{count} prijave nisu prihvaćene.',
  'queue.rejected.many': '{count} prijava nije prihvaćeno.',

  // ---- Support session ----------------------------------------------
  'support.badge': 'Podrška',
  'support.leave': 'Izađi iz sesije',
  'support.ended': 'Sesija podrške je završena.',
  'support.endFailed': 'Nije moguće završiti sesiju podrške.',

  // ---- Crash and boot -----------------------------------------------
  'error.technicalDetails': 'Tehnički detalji',
  'error.goHome': 'Početna',
  'boot.starting': 'Pokretanje aplikacije',
  'boot.loadingLayout': 'Učitavanje izgleda',
  'boot.checkingSession': 'Provjera sesije',
  'boot.aria': 'Učitavanje: {step}',
  'boot.ready': 'Spremno',
  'boot.loadingAssets': 'Učitavanje resursa',

  /*
   * Modules the owner can switch off for a company.
   *
   * The names are shared with the owner console's switches, so the module a
   * worker is told is off and the one the owner turned off are called the
   * same thing — which is the whole of the phone call that follows. The empty
   * state says nothing is broken and nothing was lost, because both are true
   * and both are what somebody fears first.
   */
  'module.submissions': 'Prijave i zadaci',
  'module.fiveS': '5S',
  'module.analytics': 'Analitika',
  'module.mes': 'KaproMES integracija',
  'module.disabled.title': 'Ovaj modul nije uključen za vašu firmu.',
  'module.disabled.description':
    '„{module}“ trenutno nije dio vaše aplikacije. Podaci su sačuvani — ako vam ovaj dio treba, obratite se svom administratoru.',
  'module.disabled.home': 'Na početnu',
} as const

export type ShellSlice = Record<keyof typeof shell, string>
